!(function () {
  "use strict";

  if (customElements.get("range-slider")) return;

  customElements.define(
    "range-slider",
    class extends HTMLElement {
      constructor() {
        super();
        this.resizeFilters = window.theme.debounce(this.handleResize.bind(this), 50);
        this.onMoveEvent = (event) => this.onMove(event);
        this.onStopEvent = (event) => this.onStop(event);
        this.onStartEvent = (event) => this.onStart(event);

        this.startX = 0;
        this.x = 0;
        this.step = 0;
        this.normalizeFact = 20;
        this._initFrame = null;
      }

      connectedCallback() {
        this.touchLeft = this.querySelector("[data-range-left]");
        this.touchRight = this.querySelector("[data-range-right]");
        this.lineSpan = this.querySelector("[data-range-line]");

        if (!this.touchLeft || !this.touchRight || !this.lineSpan) return;

        this.min = parseFloat(this.getAttribute("data-se-min"));
        this.max = parseFloat(this.getAttribute("data-se-max"));

        if (Number.isNaN(this.min)) this.min = 0;
        if (Number.isNaN(this.max) || this.max < this.min) this.max = this.min;

        let minValue = this.min;
        if (this.hasAttribute("data-se-min-value")) {
          minValue = parseFloat(this.getAttribute("data-se-min-value"));
        }

        let maxValue = this.max;
        if (this.hasAttribute("data-se-max-value")) {
          maxValue = parseFloat(this.getAttribute("data-se-max-value"));
        }

        if (Number.isNaN(minValue)) minValue = this.min;
        if (Number.isNaN(maxValue)) maxValue = this.max;
        if (minValue < this.min) minValue = this.min;
        if (maxValue > this.max) maxValue = this.max;
        if (minValue > maxValue) minValue = maxValue;

        if (this.getAttribute("data-se-step")) {
          this.step = Math.abs(parseFloat(this.getAttribute("data-se-step")));
        }
        if (Number.isNaN(this.step)) this.step = 0;

        this.selectedTouch = null;

        document.addEventListener("theme:resize", this.resizeFilters);
        this.touchLeft.addEventListener("mousedown", this.onStartEvent);
        this.touchRight.addEventListener("mousedown", this.onStartEvent);
        this.touchLeft.addEventListener("touchstart", this.onStartEvent);
        this.touchRight.addEventListener("touchstart", this.onStartEvent);

        this.initializeWhenReady(minValue, maxValue);
      }

      initializeWhenReady(minValue, maxValue) {
        if (this._initFrame) cancelAnimationFrame(this._initFrame);

        const tryInitialize = () => {
          const width = this.offsetWidth;
          if (width <= 0) {
            this._initFrame = requestAnimationFrame(tryInitialize);
            return;
          }

          this.reset();
          this.setMinValue(minValue);
          this.setMaxValue(maxValue);
          this.onChange(this.getAttribute("data-se-min-value"), this.getAttribute("data-se-max-value"));

          // Ensure first visual paint is synced after dynamic filter container sizing.
          this._initFrame = requestAnimationFrame(() => {
            this.handleResize();
            this.classList.add("is-initialized");
          });
        };

        tryInitialize();
      }

      handleResize() {
        if (!this.touchLeft || !this.touchRight || !this.lineSpan) return;

        const width = this.offsetWidth;
        if (width <= 0) return;

        const minValue = parseFloat(this.getAttribute("data-se-min-value"));
        const maxValue = parseFloat(this.getAttribute("data-se-max-value"));

        this.maxX = width - this.touchRight.offsetWidth;
        this.initialValue = width - this.touchLeft.offsetWidth - this.normalizeFact;

        this.setMinValue(Number.isNaN(minValue) ? this.min : minValue);
        this.setMaxValue(Number.isNaN(maxValue) ? this.max : maxValue);
      }

      reset() {
        const width = this.offsetWidth;
        if (width <= 0) return;

        this.touchLeft.style.left = "0px";
        this.touchRight.style.left = `${width - this.touchLeft.offsetWidth}px`;
        this.lineSpan.style.marginLeft = "0px";
        this.lineSpan.style.width = `${width - this.touchLeft.offsetWidth}px`;
        this.startX = 0;
        this.x = 0;
        this.maxX = width - this.touchRight.offsetWidth;
        this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;
      }

      setMinValue(value) {
        const range = this.max - this.min;
        const ratio = range === 0 ? 0 : (value - this.min) / range;
        const sliderWidth = this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact);

        this.touchLeft.style.left = `${Math.ceil(ratio * sliderWidth)}px`;
        this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
        this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;
        this.setAttribute("data-se-min-value", value);
      }

      setMaxValue(value) {
        const range = this.max - this.min;
        const ratio = range === 0 ? 0 : (value - this.min) / range;
        const sliderWidth = this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact);

        this.touchRight.style.left = `${Math.ceil(ratio * sliderWidth + this.normalizeFact)}px`;
        this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
        this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;
        this.setAttribute("data-se-max-value", value);
      }

      onStart(event) {
        event.preventDefault();

        let movingEvent = event;
        if (event.touches) movingEvent = event.touches[0];

        if (event.currentTarget === this.touchLeft) {
          this.x = this.touchLeft.offsetLeft;
        } else if (event.currentTarget === this.touchRight) {
          this.x = this.touchRight.offsetLeft;
        }

        this.startX = movingEvent.pageX - this.x;
        this.selectedTouch = event.currentTarget;

        document.addEventListener("mousemove", this.onMoveEvent);
        document.addEventListener("mouseup", this.onStopEvent);
        document.addEventListener("touchmove", this.onMoveEvent);
        document.addEventListener("touchend", this.onStopEvent);
      }

      onMove(event) {
        let movingEvent = event;
        if (event.touches) movingEvent = event.touches[0];

        this.x = movingEvent.pageX - this.startX;

        if (this.selectedTouch === this.touchLeft) {
          if (this.x > this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10) {
            this.x = this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10;
          } else if (this.x < 0) {
            this.x = 0;
          }
          this.selectedTouch.style.left = `${this.x}px`;
        } else if (this.selectedTouch === this.touchRight) {
          if (this.x < this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10) {
            this.x = this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10;
          } else if (this.x > this.maxX) {
            this.x = this.maxX;
          }
          this.selectedTouch.style.left = `${this.x}px`;
        }

        this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
        this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;

        this.calculateValue();

        if (this.getAttribute("on-change")) {
          // eslint-disable-next-line no-new-func
          new Function("min, max", this.getAttribute("on-change"))(
            this.getAttribute("data-se-min-value"),
            this.getAttribute("data-se-max-value")
          );
        }

        this.onChange(this.getAttribute("data-se-min-value"), this.getAttribute("data-se-max-value"));
      }

      onStop() {
        document.removeEventListener("mousemove", this.onMoveEvent);
        document.removeEventListener("mouseup", this.onStopEvent);
        document.removeEventListener("touchmove", this.onMoveEvent);
        document.removeEventListener("touchend", this.onStopEvent);

        this.selectedTouch = null;
        this.calculateValue();
        this.onChanged(this.getAttribute("data-se-min-value"), this.getAttribute("data-se-max-value"));
      }

      onChange(minValue, maxValue) {
        const holder = this.closest("[data-range-holder]");
        if (!holder) return;

        const inputMin = holder.querySelector("[data-field-price-min]");
        const inputMax = holder.querySelector("[data-field-price-max]");

        if (inputMin && inputMax) {
          inputMin.value = parseInt(minValue, 10);
          inputMax.value = parseInt(maxValue, 10);
        }
      }

      onChanged() {
        if (!this.hasAttribute("data-range-filter-update")) return;

        this.dispatchEvent(
          new CustomEvent("theme:filter:range-update", {
            bubbles: true,
          })
        );
      }

      calculateValue() {
        if (!this.initialValue || this.initialValue <= 0) return;

        const ratioWidth = (this.lineSpan.offsetWidth - this.normalizeFact) / this.initialValue;
        let ratioLeft = this.lineSpan.offsetLeft / this.initialValue;
        let ratioRight = ratioLeft + ratioWidth;

        let minValue = ratioLeft * (this.max - this.min) + this.min;
        let maxValue = ratioRight * (this.max - this.min) + this.min;

        if (this.step !== 0) {
          minValue = this.step * Math.floor(minValue / this.step);
          maxValue = this.step * Math.floor(maxValue / this.step);
        }

        if (this.selectedTouch === this.touchLeft) {
          this.setAttribute("data-se-min-value", minValue);
        }
        if (this.selectedTouch === this.touchRight) {
          this.setAttribute("data-se-max-value", maxValue);
        }
      }

      disconnectedCallback() {
        if (this._initFrame) cancelAnimationFrame(this._initFrame);

        document.removeEventListener("theme:resize", this.resizeFilters);
        document.removeEventListener("mousemove", this.onMoveEvent);
        document.removeEventListener("mouseup", this.onStopEvent);
        document.removeEventListener("touchmove", this.onMoveEvent);
        document.removeEventListener("touchend", this.onStopEvent);

        if (this.touchLeft && this.touchRight) {
          this.touchLeft.removeEventListener("mousedown", this.onStartEvent);
          this.touchRight.removeEventListener("mousedown", this.onStartEvent);
          this.touchLeft.removeEventListener("touchstart", this.onStartEvent);
          this.touchRight.removeEventListener("touchstart", this.onStartEvent);
        }
      }
    }
  );
})();
